import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
    GraduationCap,
    Trash2,
} from 'lucide-react';
import RecurrenceManager from './RecurrenceManager';
import { useMediaQuery } from '../../../hooks/useMediaQuery';

export default function CoursesList({ courses, onCourseUpdated, onCourseDeleted, readOnly = false }) {
    const { getAuthenticatedApi } = useAuth();
    const { t } = useTranslation();
    const isMobile = useMediaQuery('(max-width: 767px)');

    const [selectedCourse, setSelectedCourse] = useState(null);
    const [detailTab, setDetailTab] = useState('dates');

    useEffect(() => {
        if (!courses?.length) {
            setSelectedCourse(null);
            return;
        }

        setSelectedCourse((prev) => {
            if (prev) {
                const updated = courses.find((course) => Number(course.id) === Number(prev.id));
                if (updated) return updated;
            }
            // Desktop: seleziona il primo; mobile: nessuna preselezione
            return isMobile ? null : courses[0];
        });
    }, [courses, isMobile]);

    const handleDelete = async (course) => {
        if (readOnly || !course) return;
        if (!window.confirm(t('gym.courses.list.deleteConfirm', { title: course.title }))) {
            return;
        }

        try {
            const api = getAuthenticatedApi();
            await api.delete(`/gym_courses.php?action=delete_course&id=${course.id}`);
            toast.success(t('gym.courses.list.deleteSuccess'));
            onCourseDeleted(course.id);
            setSelectedCourse(null);
        } catch (err) {
            console.error('Errore eliminazione corso:', err);
            toast.error(t('gym.courses.errors.deleteError'));
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            active: { bg: 'bg-green-100 dark:bg-green-900/20', text: 'text-green-800 dark:text-green-400', label: t('gym.courses.status.active') },
            paused: { bg: 'bg-yellow-100 dark:bg-yellow-900/20', text: 'text-yellow-800 dark:text-yellow-400', label: t('gym.courses.status.paused') },
            cancelled: { bg: 'bg-red-100 dark:bg-red-900/20', text: 'text-red-800 dark:text-red-400', label: t('gym.courses.status.cancelled') }
        };

        const badge = badges[status] || badges.active;

        return (
            <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${badge.bg} ${badge.text}`}>
                {badge.label}
            </span>
        );
    };

    const getCapacityText = (course) => (
        course.is_unlimited
            ? t('gym.courses.list.unlimited')
            : (course.max_participants || t('gym.courses.list.notSet'))
    );

    const showList = !isMobile || !selectedCourse;
    const showDetail = !isMobile || Boolean(selectedCourse);

    const courseList = (
        <div className={`${isMobile ? 'flex h-full min-h-0 flex-col' : 'col-span-3 flex h-full min-h-0 flex-col'} border-r border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800`}>
            <div className="flex-1 overflow-y-auto dark:bg-gray-900">
                {courses.length > 0 ? (
                    <div className="space-y-2 p-2">
                        {courses.map((course) => {
                            const isSelected = Number(selectedCourse?.id) === Number(course.id);
                            return (
                                <button
                                    key={course.id}
                                    type="button"
                                    onClick={() => {
                                        setSelectedCourse(course);
                                        setDetailTab('dates');
                                    }}
                                    className={`w-full rounded-lg border-2 p-3 text-left transition-colors ${
                                        isSelected
                                            ? 'border-indigo-500 bg-indigo-50 dark:border-indigo-500 dark:bg-indigo-950/50'
                                            : 'border-transparent hover:bg-gray-50 dark:hover:bg-white/10'
                                    }`}
                                >
                                    <div className="flex items-start gap-3">
                                        {course.logo_filename ? (
                                            <img
                                                src={`/api/serve_image.php?filename=${course.logo_filename}&type=logo`}
                                                alt=""
                                                className="h-9 w-9 flex-shrink-0 rounded-lg border border-gray-200 object-cover dark:border-white/15"
                                            />
                                        ) : (
                                            <div
                                                className="mt-1.5 h-3 w-3 flex-shrink-0 rounded-full"
                                                style={{ backgroundColor: course.color || '#3B82F6' }}
                                            />
                                        )}
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2">
                                                <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                                                    {course.title}
                                                </p>
                                                {getStatusBadge(course.status)}
                                            </div>
                                            <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                                {course.category && (
                                                    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-700 dark:bg-gray-800 dark:text-white/80">
                                                        {course.category}
                                                    </span>
                                                )}
                                                <span className="text-[11px] text-gray-500 dark:text-white/55">
                                                    Max {getCapacityText(course)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                ) : (
                    <div className="flex h-full flex-col items-center justify-center px-4 py-10 text-center text-gray-500 dark:text-white/70">
                        <GraduationCap size={40} className="mb-3 opacity-50" />
                        <p className="text-sm font-medium">{t('gym.courses.emptyState.noCoursesFound')}</p>
                    </div>
                )}
            </div>
        </div>
    );

    const detailPanel = (
        <div className={`${isMobile ? 'flex h-full min-h-0 flex-col' : 'col-span-9 flex h-full min-h-0 flex-col'} overflow-hidden bg-white dark:bg-gray-800`}>
            {selectedCourse ? (
                <RecurrenceManager
                    embedded
                    isOpen
                    course={selectedCourse}
                    enrollmentOnly={readOnly}
                    initialTab={readOnly ? 'dates' : detailTab}
                    onClose={isMobile ? () => setSelectedCourse(null) : undefined}
                    onRecurrenceUpdated={onCourseUpdated}
                    headerActions={readOnly ? null : (
                        <button
                            type="button"
                            onClick={() => handleDelete(selectedCourse)}
                            className="rounded-lg p-2 text-red-600 transition-colors hover:bg-red-50 dark:hover:bg-red-900/20"
                            title={t('gym.courses.list.delete')}
                        >
                            <Trash2 size={18} />
                        </button>
                    )}
                />
            ) : (
                <div className="flex h-full flex-col items-center justify-center px-6 text-center text-gray-500 dark:text-white/70">
                    <GraduationCap size={48} className="mb-4 opacity-40" />
                    <p className="font-medium">{t('gym.courses.selectCourse')}</p>
                </div>
            )}
        </div>
    );

    if (isMobile) {
        return (
            <div className="h-full min-h-0 overflow-hidden rounded-lg border border-gray-200 dark:border-white/10">
                {showList ? courseList : null}
                {showDetail ? detailPanel : null}
            </div>
        );
    }

    return (
        <div className="grid h-full min-h-0 grid-cols-12 overflow-hidden rounded-lg border border-gray-200 dark:border-white/10">
            {courseList}
            {detailPanel}
        </div>
    );
}

